/**
 * Codes-barres — validation pure, sans React ni I/O.
 *
 * Extrait de `components/scanner/BarcodeScanner.tsx` quand la **saisie manuelle**
 * a été ajoutée : les deux voies d'entrée d'un code doivent le juger de la même
 * façon. Même raisonnement que `lib/formFields.ts` à l'étape 6 — dupliquer une
 * somme de contrôle, c'est garantir qu'un jour les deux copies diverge­ront et
 * qu'un code sera accepté d'un côté, refusé de l'autre.
 */

/** Longueurs des formats que l'application scanne (EAN-8, UPC-A, EAN-13). */
const KNOWN_LENGTHS = [8, 12, 13];

/**
 * Somme de contrôle EAN-13 / EAN-8 / UPC-A.
 *
 * Vérification **indépendante du décodeur** : elle attrape les lectures
 * partielles que ZXing juge valides, et les fautes de frappe à la saisie
 * manuelle. `null` si le format ne s'y prête pas — ce qui n'est pas un échec,
 * seulement un code dont on ne sait rien dire.
 */
export function eanChecksumValid(code: string): boolean | null {
  if (!/^\d+$/.test(code)) return null;
  if (!KNOWN_LENGTHS.includes(code.length)) return null;
  const digits = [...code].map(Number);
  const check = digits.pop() ?? 0;
  let sum = 0;
  // Le poids 3 s'applique en partant de la droite du corps du code.
  for (
    let index = digits.length - 1, weight = 3;
    index >= 0;
    index--, weight = weight === 3 ? 1 : 3
  ) {
    sum += (digits[index] ?? 0) * weight;
  }
  return (10 - (sum % 10)) % 10 === check;
}

/** Ne garde que les chiffres — colle depuis une étiquette, espaces compris. */
export function normalizeBarcode(raw: string): string {
  return raw.replace(/\D/g, '');
}

/** Verdict d'un code **saisi à la main**. */
export type ManualCodeVerdict =
  | { state: 'empty' }
  /** Longueur qui ne correspond à aucun format connu : on n'interroge pas. */
  | { state: 'wrong-length' }
  /** Longueur correcte mais somme de contrôle fausse — probablement une faute de frappe. */
  | { state: 'bad-checksum' }
  | { state: 'ok' };

/**
 * Juge un code saisi à la main.
 *
 * ⚠️ **La somme de contrôle avertit, elle ne bloque pas.** Un code recopié d'une
 * étiquette abîmée peut être juste sans que notre calcul le confirme, et un
 * produit peut porter un code hors norme ; refuser d'interroger reviendrait à
 * décider à la place de l'utilisateur. En revanche une longueur aberrante — 5 ou
 * 20 chiffres — ne vaut pas un aller-retour réseau : ce n'est pas un code-barres,
 * c'est une saisie en cours.
 */
export function judgeManualCode(raw: string): ManualCodeVerdict {
  const code = normalizeBarcode(raw);
  if (code === '') return { state: 'empty' };
  if (!KNOWN_LENGTHS.includes(code.length)) return { state: 'wrong-length' };
  return eanChecksumValid(code) === false ? { state: 'bad-checksum' } : { state: 'ok' };
}
